import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { formatRupiah } from './formatRupiah';

const printPdfReportItem = {
  /**
   * Generate sales by category PDF report
   * @param {Object} options - Report options
   * @param {string} options.companyName - Company name
   * @param {string} options.companyAddress - Company address
   * @param {string} options.companyPhone - Company phone
   * @param {Array} report - Report data
   * @param {boolean} showQty - Whether to show quantity
   * @param {boolean} showRevenue - Whether to show revenue
   */
  generateSalesByCategoryReport(report, options = {}) {
    const defaultOptions = {
      title: 'Laporan Penjualan Per Kategori',
      companyName: process.env.VUE_APP_STORE_NAME || 'Nama Toko',
      companyAddress: process.env.VUE_APP_STORE_ADDRESS || 'Alamat Toko',
      companyPhone: process.env.VUE_APP_STORE_PHONE || 'Telepon',
      ...options
    };

    // Create new PDF document
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Add header
    this.addHeader(doc, defaultOptions, true);

    // Add title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('LAPORAN PENJUALAN PER KATEGORI', 105, 30, { align: 'center' });

    // Add print date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Dicetak: ${new Date().toLocaleString('id-ID')}`, 14, 40);

    let startY = 50;

    // Add each category
    report?.forEach((category, catIndex) => {
      // Check if we need a new page before adding category header
      if (startY > 250) {
        doc.addPage();
        this.addHeader(doc, defaultOptions, false);
        startY = 30;
      }

      // Add category header
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(category?.category_name ? category?.category_name?.toUpperCase() : '', 14, startY);
      startY += 8; // Space after category title

      // Prepare table data
      let tableData = category?.items?.map((item, index) => [
        index + 1,
        item.name,
        item?.qty || 0,
        formatRupiah(item?.revenue_item || 0),
        formatRupiah(item?.item_discount || 0),
        formatRupiah(item?.revenue_sales || 0),
        formatRupiah(item?.bill_discount || 0),
        formatRupiah(item?.revenue_net_sales || 0),
        formatRupiah(item?.tax || 0),
        formatRupiah(item?.service_charge || 0),
        formatRupiah(item?.gross_sales || 0),
      ]);

      tableData.push([
        'Total',
        '',
        category?.qty || 0,
        formatRupiah(category?.revenue_item || 0),
        formatRupiah(category?.item_discount || 0),
        formatRupiah(category?.revenue_sales || 0),
        formatRupiah(category?.bill_discount || 0),
        formatRupiah(category?.revenue_net_sales || 0),
        formatRupiah(category?.tax || 0),
        formatRupiah(category?.service_charge || 0),
        formatRupiah(category?.gross_sales || 0),
      ]);

      // Define columns
      const columns = [
        { header: 'No', dataKey: 0, cellWidth: 10 },
        { header: 'Nama Item', dataKey: 1, cellWidth: 40 },
      ];

      columns.push({ header: 'Qty', dataKey: 2, cellWidth: 10, halign: 'right' });
      columns.push({ header: 'Revenue Item', dataKey: 3, cellWidth: 25, halign: 'right' });
      columns.push({ header: 'Discount Item', dataKey: 4, cellWidth: 25, halign: 'right' });
      columns.push({ header: 'Revenue Sales', dataKey: 6, cellWidth: 25, halign: 'right' });
      columns.push({ header: 'Discount Bill', dataKey: 5, cellWidth: 25, halign: 'right' });
      columns.push({ header: 'Revenue Net Sales', dataKey: 9, cellWidth: 25, halign: 'right' });
      columns.push({ header: 'Tax', dataKey: 7, cellWidth: 25, halign: 'right' });
      columns.push({ header: 'Service Charge', dataKey: 8, cellWidth: 25, halign: 'right' });
      columns.push({ header: 'Gross Sales', dataKey: 10, cellWidth: 25, halign: 'right' });

      // Add table with page break handling
      doc.autoTable({
        head: [columns.map(col => col.header)],
        body: tableData,
        startY,
        margin: { 
          left: 14, 
          right: 14,
          top: 10,
          bottom: 20 // Extra bottom margin to prevent footer overlap
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8,
          minCellHeight: 8
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 2,
          minCellHeight: 8
        },
        styles: {
          overflow: 'linebreak',
          lineWidth: 0.1
        },
        columnStyles: columns.reduce((acc, col, idx) => {
          acc[idx] = { 
            cellWidth: col.cellWidth,
            halign: col.halign || 'left',
            valign: 'middle'
          };
          return acc;
        }, {}),
        didDrawPage: function(data) {
          // Update startY after each page is drawn
          startY = data.cursor.y;
        }
      });

      // Update Y position after table is drawn
      startY = doc.lastAutoTable.finalY + 10;

      // Add page break if we're too close to the bottom for the next category
      if (startY > 250 && catIndex < report.length - 1) {
        doc.addPage();
        this.addHeader(doc, defaultOptions);
        startY = 30; // Reset Y position for new page
      }
    });

    // Add footer
    this.addFooter(doc);

    // Save the PDF
    const fileName = `${defaultOptions.title}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  },

  /**
   * Add header to the PDF
   * @param {Object} doc - jsPDF document instance
   * @param {Object} options - Header options
   */
  addHeader(doc, options, isFirstPage = false) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const startY = isFirstPage ? 15 : 15; // Start higher on subsequent pages
    
    // Company Name
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(options.companyName, pageWidth / 2, 10, { align: 'center' });
    
    // Company Address
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(options.companyAddress, pageWidth / 2, 15, { align: 'center' });
    
    // Company Phone
    doc.text(options.companyPhone, pageWidth / 2, 20, { align: 'center' });

    // Date Range on left side
    doc.setFontSize(9);
    doc.text(`Periode: ${options.startDate} s/d ${options.endDate}`, 20, startY + 5);

    // Page number on right side for all pages
    const pageCount = doc.internal.getNumberOfPages();
    doc.text(`Halaman ${pageCount}`, pageWidth - 20, startY + 5, { align: 'right' });
    
    // Line separator
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(20, 25, pageWidth - 20, 25);
  },

  /**
   * Add footer to the PDF
   * @param {Object} doc - jsPDF document instance
   */
  addFooter(doc) {
    const pageCount = doc.internal.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Page number
      doc.setFontSize(8);
      doc.text(
        `Halaman ${i} dari ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
  }
};

export default printPdfReportItem;